<template>
  <div class="col-12" data-testid="card_change">
    <div class="row">
      <div class="col">
        <h3 class="mt-3">{{ $t('message.card.title') }}</h3>
        <div class="btn-group mb-3">
          <button :class="{btn: true, 'btn-primary': $root.$i18n.locale == 'ja', 'btn-light': $root.$i18n.locale != 'ja'}" @click.prevent="changeLang('ja')">日本語</button>
          <button :class="{btn: true, 'btn-primary': $root.$i18n.locale == 'en', 'btn-light': $root.$i18n.locale != 'en'}" @click.prevent="changeLang('en')">English</button>
          <button :class="{btn: true, 'btn-primary': $root.$i18n.locale == 'cn', 'btn-light': $root.$i18n.locale != 'cn'}" @click.prevent="changeLang('cn')">中文</button>
        </div>
        <hr>
      </div>
    </div>

    <!-- 入口: レンタル番号＋メールで照合 -->
    <CardLookup v-if="!$root.key" @found="onFound" />

    <template v-else>
      <!-- 結果: load() が失敗しても消えないよう status/loadError と独立して表示する -->
      <div v-if="result" class="alert" :class="result.ok ? 'alert-success' : 'alert-warning'" role="alert" data-testid="card_change_result" :data-teststate="resultState">
        {{ $t('message.card.result_' + resultState) }}
      </div>

      <div v-if="loading" data-testid="card_change_loading"><i class="fas fa-spinner fa-spin"></i></div>

      <div v-else-if="loadError" class="text-danger" role="alert" data-testid="card_change_error">{{ $t(loadError) }}</div>

      <template v-else-if="status">
        <div class="row text-left" data-testid="card_change_summary">
          <div class="col-12 col-md-8 offset-md-2">
            <table class="table table-sm">
              <tbody>
                <tr><th>{{ $t('レンタルNo.') }}</th>
                    <td>{{ status.rental_no }}<small v-if="status.former_rental_no" class="text-muted ml-2">({{ $t('message.card.former_no') }} {{ status.former_rental_no }})</small></td></tr>
                <tr><th>{{ $t('message.rentalDate') }}</th><td>{{ status.start }}</td></tr>
                <tr v-if="status.amount !== null"><th>{{ $t('合計') }}</th><td>¥{{ status.amount }}</td></tr>
                <tr><th>{{ $t('message.card.current_card') }}</th>
                    <td v-if="status.card">{{ status.card.brand }} **** {{ status.card.last4 }}</td>
                    <td v-else>-</td></tr>
              </tbody>
            </table>

            <div class="alert" :class="stateClass" role="status" data-testid="card_change_state" :data-teststate="status.payment_state">
              {{ $t('message.card.state_' + status.payment_state) }}
              <div v-if="status.can_replace && status.will_authorize_now"><small>{{ $t('message.card.authorize_now_note') }}</small></div>
              <div v-if="status.can_replace && !status.will_authorize_now"><small>{{ $t('message.card.authorize_later_note') }}</small></div>
              <div v-if="status.can_replace && status.deadline_at"><small>{{ $t('message.card.deadline_note') }} {{ status.deadline_at }}</small></div>
            </div>

            <!-- 差し替えフォーム -->
            <template v-if="status.can_replace">
              <h5 class="mt-4">{{ $t('message.card.new_card') }}</h5>
              <div class="text-danger" role="alert" v-if="apiError" data-testid="card_change_api_error">{{ $t(apiError) }}</div>
              <SquareCardForm v-if="formVisible"
                              :application-id="$root.serviceSetting.square_application_id"
                              :location-id="$root.serviceSetting.square_location_id"
                              :environment="$root.serviceSetting.square_environment"
                              :locale="$root.$i18n.locale"
                              :buyer="{}"
                              :disabled="submitting"
                              container-id="square-card-container-change"
                              @tokenized="onTokenized">
                {{ $t('message.card.submit') }}
              </SquareCardForm>
            </template>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
// #226 与信NG客のお支払いカード確認・変更画面。
// 状態は square_card_status が決める（締め超過/コンバート済み/与信済みの出し分けをフロントで再計算しない）。
import axios from 'axios'
import CardLookup from './CardLookup.vue'
import SquareCardForm from './SquareCardForm.vue'

export default {
  name: 'CardChange',
  components: { CardLookup, SquareCardForm },
  data () {
    return { loading: false, loadError: '', status: null, formVisible: false, submitting: false, apiError: '', result: null }
  },
  computed: {
    stateClass () {
      if (!this.status) return 'alert-secondary'
      switch (this.status.payment_state) {
        case 'SECURED': return 'alert-success'
        case 'FAILED': return 'alert-danger'
        case 'WAITING': return 'alert-info'
        default: return 'alert-secondary' // CONVERTED / CLOSED / NO_TRANSACTION / CONFLICT
      }
    },
    resultState () {
      if (!this.result) return ''
      if (this.result.ok && this.result.authorized) return 'authorized'
      if (this.result.ok) return 'replaced_only'
      if (this.result.error === 'authorization_declined') return 'authorization_declined'
      if (this.result.error === 'card_declined') return 'card_declined'
      if (this.result.error === 'conflict' || this.result.error === 'not_replaceable') return 'state_changed'
      return 'error'
    }
  },
  created () {
    if (this.$root.key) this.load()
  },
  watch: {
    '$root.key' (v) {
      if (v) this.load()
    }
  },
  methods: {
    changeLang (lang) {
      this.$root.$i18n.locale = lang
      this.$root.$store.commit('update', { field: 'lang', value: lang })
    },
    onFound (hash) {
      this.$root.key = hash
      this.$router.replace({ name: 'card', params: { key: hash } })
    },
    async load () {
      this.loading = true
      this.loadError = ''
      this.formVisible = false
      try {
        const url = this.$root.baseURL + 'square_card_status.json'
        const response = await axios.post(url, { key: this.$root.key },
          { headers: { 'Content-Type': 'application/json' }, responseType: 'json', timeout: 20000 })
        if (!response.data || !response.data.ok) throw new Error('not ok')
        this.status = response.data
        this.formVisible = !!this.status.can_replace
      } catch (e) {
        this.status = null
        this.loadError = 'message.card.load_error'
      } finally {
        this.loading = false
      }
    },
    async onTokenized (tok) {
      if (this.submitting) return
      this.submitting = true
      this.apiError = ''
      this.result = null
      try {
        const url = this.$root.baseURL + 'square_replace_card.json'
        const response = await axios.post(url, { key: this.$root.key, nonce: tok.nonce, verificationToken: tok.verificationToken },
          {
            headers: { 'Content-Type': 'application/json' },
            responseType: 'json',
            timeout: 60000,
            validateStatus: () => true // 402/409 も body を読む
          })
        this.result = response.data || { ok: false, error: 'internal_error' }
      } catch (e) {
        this.result = { ok: false, error: 'internal_error' }
      } finally {
        this.submitting = false
        await this.load() // 結果後の状態（SECURED / FAILED / 変わらず）を API から取り直す
      }
    }
  }
}
</script>
